import { useEffect, useRef, useState } from "react";
import { Bot, Send, X, Sparkles, Loader2 } from "lucide-react";
import { contactInfo } from "@/data/machines";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const QUICK_ACTIONS = [
  { label: "¿Qué máquina necesito?", prompt: "Necesito ayuda para elegir la máquina adecuada. Voy a describirte mi trabajo." },
  { label: "Trabajo en altura", prompt: "Necesito trabajar en altura. ¿Qué plataforma me recomiendas?" },
  { label: "Excavar / mover tierra", prompt: "Tengo que excavar y mover tierra. ¿Qué máquina me recomiendas?" },
  { label: "Disponibilidad", prompt: "¿Tenéis disponibilidad esta semana?" },
];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content:
        "¡Hola! Soy el asistente de Maquinaria Santacruz. Cuéntame qué trabajo tienes que hacer y te recomiendo la máquina perfecta. También puedo ayudarte con disponibilidad, entrega y dudas técnicas.",
    },
  ]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(() => {
    try {
      return localStorage.getItem("ms_chat_session") || null;
    } catch {
      return null;
    }
  });
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const persistSession = (sid) => {
    setSessionId(sid);
    try {
      localStorage.setItem("ms_chat_session", sid);
    } catch {
      /* ignore */
    }
  };

  const sendMessage = async (text) => {
    if (!text.trim() || sending) return;
    const userMsg = { role: "user", content: text.trim() };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    setInput("");
    setSending(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${BACKEND}/api/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text.trim() }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const jsonStr = line.slice(5).trim();
          if (!jsonStr) continue;
          try {
            const evt = JSON.parse(jsonStr);
            if (evt.type === "delta") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  ...copy[copy.length - 1],
                  content: (copy[copy.length - 1].content || "") + evt.content,
                };
                return copy;
              });
            } else if (evt.type === "done") {
              if (evt.session_id) persistSession(evt.session_id);
            } else if (evt.type === "error") {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: "Vaya, algo ha fallado. Prueba de nuevo o escríbenos por WhatsApp al " + contactInfo.phone,
                };
                return copy;
              });
            }
          } catch {
            /* ignore parse errors */
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        const last = copy[copy.length - 1];
        if (last && last.role === "assistant" && !last.content) {
          copy[copy.length - 1] = {
            role: "assistant",
            content:
              "No he podido conectar ahora mismo. Escríbenos por WhatsApp al " +
              contactInfo.phone +
              " y te atendemos al momento.",
          };
        }
        return copy;
      });
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <>
      {/* Launcher bubble */}
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-launcher"
        aria-label={open ? "Cerrar asistente" : "Abrir asistente"}
        className="fixed bottom-5 left-5 md:bottom-6 md:left-6 z-50 group inline-flex items-center gap-2 pl-3 pr-4 py-3 bg-slate-950 hover:bg-slate-800 text-white rounded-full shadow-2xl shadow-slate-950/40 hover:scale-105 transition-transform duration-300"
      >
        <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-sky-500">
          {open ? <X className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
          {!open && (
            <span aria-hidden className="absolute inset-0 rounded-full bg-sky-500 opacity-60 animate-ping -z-10" />
          )}
        </span>
        <span className="hidden sm:inline text-sm font-semibold pr-1">
          {open ? "Cerrar" : "Asistente IA"}
        </span>
      </button>

      {/* Chat panel */}
      {open && (
        <div
          data-testid="chat-panel"
          className="fixed z-50 inset-x-3 bottom-24 md:inset-auto md:bottom-24 md:left-6 md:w-[380px] max-h-[75vh] md:max-h-[600px] bg-white rounded-2xl shadow-2xl shadow-slate-950/30 border border-slate-200 flex flex-col overflow-hidden animate-[fadeIn_.2s_ease-out]"
        >
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-slate-950 to-slate-800 text-white">
            <div className="relative w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center">
              <Sparkles className="w-5 h-5" />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-slate-950" />
            </div>
            <div className="leading-tight flex-1">
              <div className="text-sm font-bold">Asistente Maquinaria Santacruz</div>
              <div className="text-[11px] text-slate-300">Respuestas al momento · IA</div>
            </div>
            <button
              onClick={() => setOpen(false)}
              aria-label="Cerrar"
              data-testid="chat-close"
              className="p-1.5 rounded-lg hover:bg-white/10"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            data-testid="chat-messages"
            className="flex-1 overflow-y-auto px-3 py-4 space-y-3 bg-slate-50"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-sky-500 text-white rounded-br-sm"
                      : "bg-white text-slate-800 border border-slate-200 rounded-bl-sm shadow-sm"
                  }`}
                >
                  {m.content || (
                    <span className="inline-flex items-center gap-1.5 text-slate-400">
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pensando…
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Quick actions */}
          {messages.length <= 1 && (
            <div className="px-3 pt-2 pb-1 flex flex-wrap gap-1.5 bg-slate-50 border-t border-slate-100">
              {QUICK_ACTIONS.map((q) => (
                <button
                  key={q.label}
                  onClick={() => sendMessage(q.prompt)}
                  data-testid={`chat-quick-${q.label.slice(0, 12).replace(/\s+/g, "-").toLowerCase()}`}
                  className="text-[11px] font-medium px-2.5 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-sky-50 hover:border-sky-300 hover:text-sky-700 transition-colors"
                >
                  {q.label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={onSubmit}
            className="flex items-end gap-2 p-3 bg-white border-t border-slate-200"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(input);
                }
              }}
              rows={1}
              placeholder="Escribe tu pregunta…"
              data-testid="chat-input"
              disabled={sending}
              className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 focus:border-sky-500 disabled:opacity-60 max-h-32"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="chat-send"
              aria-label="Enviar"
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-sky-500 hover:bg-sky-600 text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
